<!--
  PlatformSidebar — contextual sidebar contents for the "Platform"
  cauldron. Consolidates account-management surfaces into one cauldron,
  sitting at the bottom of the rail per the system-pane convention
  (VSCode / Notion). The developer rows moved to the API store cauldron
  (`ApiStoreSidebar.vue`).

  Two sub-sections:
    - **Account** — Profile, Tenant, Security, Preferences, Notifications.
      (Tenant users are managed on the subscription Members page.)
    - **My subscription** — Members, tokens, quotas, entitlements,
      and invoices. (Upgrade is reached via the green CTA on these pages.)
-->
<template>
  <div class="flex flex-col h-full">
    <nav class="flex-1 overflow-y-auto px-3 py-3" aria-label="Platform navigation">
      <div v-if="!collapsed" class="sidebar-section-label">
        {{ t('cauldron.platform.label') }}
      </div>

      <!-- Account section -->
      <div v-if="!collapsed" class="sidebar-section-label">
        {{ t('cauldron.platform.section_account') }}
      </div>
      <sidebar-menu-item
        to="/profile"
        :label="t('page_titles.profile')"
        :icon="mdiAccountOutline"
        :collapsed="collapsed"
        active-match="exact"
        @click="emit('navigate')"
      />

      <sidebar-menu-item
        v-if="showTenants"
        to="/tenants"
        :label="t('general_tenant')"
        :icon="mdiDomain"
        :collapsed="collapsed"
        active-match="prefix"
        @click="emit('navigate')"
      />

      <sidebar-menu-item
        to="/account/security"
        :label="t('profile.tabs.security')"
        :icon="mdiShieldAccountOutline"
        :collapsed="collapsed"
        active-match="prefix"
        @click="emit('navigate')"
      />

      <sidebar-menu-item
        to="/account/preferences"
        :label="t('profile.tabs.onboarding')"
        :icon="mdiTune"
        :collapsed="collapsed"
        active-match="prefix"
        @click="emit('navigate')"
      />

      <sidebar-menu-item
        to="/notifications/preferences"
        :label="t('notification_preferences.menu_label')"
        :icon="mdiBellOutline"
        :collapsed="collapsed"
        active-match="prefix"
        @click="emit('navigate')"
      />

      <sidebar-menu-item
        to="/account/feedback"
        :label="t('feedback.mine.menu_label')"
        :icon="mdiMessageAlertOutline"
        :collapsed="collapsed"
        active-match="prefix"
        @click="emit('navigate')"
      />

      <!-- Subscription section -->
      <template v-if="showSubscriptionSection">
        <div v-if="!collapsed" class="sidebar-section-label">
          {{ t('menu_my_subscription') }}
        </div>
        <sidebar-menu-item
          to="/platform/subscription/members"
          :label="t('subscription_nav.members')"
          :icon="mdiAccountGroupOutline"
          :collapsed="collapsed"
          active-match="prefix"
          @click="emit('navigate')"
        />
        <sidebar-menu-item
          to="/platform/subscription/tokens"
          :label="t('subscription_nav.tokens')"
          :icon="mdiCashMultiple"
          :collapsed="collapsed"
          active-match="prefix"
          @click="emit('navigate')"
        />
        <sidebar-menu-item
          to="/platform/subscription/quotas"
          :label="t('subscription_nav.quotas')"
          :icon="mdiGauge"
          :collapsed="collapsed"
          active-match="prefix"
          @click="emit('navigate')"
        />
        <sidebar-menu-item
          to="/platform/subscription/entitlements"
          :label="t('subscription_nav.entitlements')"
          :icon="mdiShieldCheckOutline"
          :collapsed="collapsed"
          active-match="prefix"
          @click="emit('navigate')"
        />
        <sidebar-menu-item
          to="/platform/subscription/invoices"
          :label="t('subscription_nav.invoices')"
          :icon="mdiFileDocumentOutline"
          :collapsed="collapsed"
          active-match="prefix"
          @click="emit('navigate')"
        />
      </template>
    </nav>
  </div>
</template>

<script setup>
  /* eslint-disable no-undef */
  import SidebarMenuItem from '@/components/navigation/SidebarMenuItem.vue';
  import {
    mdiAccountGroupOutline,
    mdiAccountOutline,
    mdiBellOutline,
  mdiMessageAlertOutline,
    mdiCashMultiple,
    mdiDomain,
    mdiFileDocumentOutline,
    mdiGauge,
    mdiShieldAccountOutline,
    mdiShieldCheckOutline,
    mdiTune,
  } from '@mdi/js';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useAuthStore } from '@/stores/useAuthStore';

  defineProps({
    collapsed: { type: Boolean, default: false },
  });
  const emit = defineEmits(['navigate']);

  const { t } = useI18n();
  const authStore = useAuthStore();

  // Auth state used for permission-gated entries. Mirrors the rules from
  // the legacy `ProfileNav` (which we're replacing) so the same set of
  // users sees the same set of links.
  const isAuthenticated = computed(() => authStore.isAuthenticated);
  const permissions = computed(() => authStore.permissions || []);

  const showSubscriptionSection = computed(() => isAuthenticated.value);
  const showTenants = computed(
    () => Boolean(authStore.tenant || authStore.tenantId) || permissions.value.includes('manage_own_tenant'),
  );
</script>
